import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/shared/Navbar';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { trackGiftRedeemed } from '../utils/analytics';
import safeStorage from '../utils/safeStorage';

const PLAN_LABELS  = { basic: 'Basic', family: 'Family' };
const PLAN_PERKS   = {
  basic:  ['Unlimited personalised bedtime stories', 'Multiple children profiles', 'Stories in any language', 'Beautiful magical illustrations'],
  family: ['Everything in Basic', 'Voice cloning — narrate in your own voice', 'Family sharing & collaboration', 'Priority generation'],
};

const GiftRedeemPage = () => {
  const { token }    = useParams();
  // refreshUser is required by handleRedeem — without it the redeem succeeds
  // server-side and then throws a ReferenceError, so the success screen never
  // shows and the user is told the redemption failed.
  const { user, refreshUser } = useAuth();
  const navigate     = useNavigate();

  const [status, setStatus]   = useState('loading'); // loading | ready | already_redeemed | expired | error
  const [gift, setGift]       = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    API.get(`/gift/subscription/${token}`)
      .then(res => {
        if (res.data.alreadyRedeemed) return setStatus('already_redeemed');
        if (res.data.expired)         return setStatus('expired');
        setGift(res.data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [token]);

  // Carry the token back here after auth. This used to rely on localStorage
  // alone — but storage throws when site data is blocked (see utils/safeStorage),
  // in which case setItem is a no-op and the gift was silently lost: the user
  // signed in, landed on the dashboard, and a subscription someone had already
  // paid for went unredeemed with nothing to explain it.
  // ?redirect= survives regardless of storage; both LoginPage and RegisterPage
  // already honour it, and each prefers the stored token when it is present, so
  // the two routes agree.
  const authPath = (page) => `/${page}?redirect=${encodeURIComponent(`/gift/redeem/${token}`)}`;
  const goToLogin = () => {
    safeStorage.setItem('pendingGiftToken', token);
    navigate(authPath('login'));
  };
  const goToRegister = () => {
    safeStorage.setItem('pendingGiftToken', token);
    navigate(authPath('register'));
  };

  const handleRedeem = async () => {
    setRedeemError(''); setRedeeming(true);
    try {
      await API.post(`/gift/subscription/${token}/redeem`);
      trackGiftRedeemed(gift?.plan);
      // Without this the context and cached user still say 'free', so the
      // dashboard shows Free limits and upsells for the plan just claimed.
      await refreshUser();
      setSuccess(true);
    } catch (err) {
      setRedeemError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" style={{ width: '3rem', height: '3rem', color: '#c9a8ff' }}></div>
          <p style={{ color: '#c9a8ff' }}>Loading your gift…</p>
        </div>
      </div>
    );
  }

  // ── Already redeemed ─────────────────────────────────────
  if (status === 'already_redeemed') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
        <Navbar />
        <div className="container py-5 text-center" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🌙</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Already redeemed</h4>
          <p className="text-muted mb-4">This gift has already been claimed. If you think this is a mistake, please contact support.</p>
          <Link to="/dashboard" className="btn btn-dream rounded-pill px-4">Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  // ── Expired ──────────────────────────────────────────────
  if (status === 'expired') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
        <Navbar />
        <div className="container py-5 text-center" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>⏰</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>This link has expired</h4>
          <p className="text-muted mb-4">Gift links are valid for 90 days. This one has passed that window.</p>
          <a href="mailto:support@dreamybedtime.com" className="btn btn-dream rounded-pill px-4">Contact Support</a>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
        <Navbar />
        <div className="container py-5 text-center" style={{ maxWidth: '480px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>😔</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Gift not found</h4>
          <p className="text-muted mb-4">This link doesn't look right. Check the email for the correct link.</p>
          <Link to="/" className="btn btn-dream rounded-pill px-4">Go Home</Link>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────
  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
        <Navbar />
        <div className="container py-5" style={{ maxWidth: '520px' }}>
          <div className="card border-0 shadow" style={{ borderRadius: '24px', overflow: 'hidden' }}>
            <div className="p-5 text-center" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
              <div style={{ fontSize: '64px', marginBottom: '12px' }}>🎉</div>
              <h2 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
                Gift activated!
              </h2>
              <p style={{ color: '#c9a8ff', fontSize: '16px', margin: 0 }}>
                Your {gift?.durationMonths}-month {PLAN_LABELS[gift?.plan]} subscription is live.
              </p>
            </div>
            <div className="p-5">
              <ul className="list-unstyled mb-4">
                {(PLAN_PERKS[gift?.plan] || []).map(perk => (
                  <li key={perk} className="d-flex align-items-center gap-3 mb-2">
                    <div className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: '28px', height: '28px', minWidth: '28px', background: 'var(--purple-section)' }}>
                      <i className="bi bi-check2" style={{ color: '#6f42c1', fontSize: '13px' }}></i>
                    </div>
                    <span className="small" style={{ color: 'var(--text-main)' }}>{perk}</span>
                  </li>
                ))}
              </ul>
              <Link to="/dashboard/generate" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold">
                <i className="bi bi-magic me-2"></i>Start creating stories
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Ready — show gift details + CTA ─────────────────────
  const planLabel = PLAN_LABELS[gift?.plan] || 'Basic';
  const months    = gift?.durationMonths || 1;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Helmet>
        <title>Redeem Your Gift | Dreamy Bedtime</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />

      <div className="container py-5" style={{ maxWidth: '520px' }}>
        <div className="card border-0 shadow" style={{ borderRadius: '24px', overflow: 'hidden' }}>

          {/* Header */}
          <div className="p-5 text-center" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
            <div style={{ fontSize: '56px', marginBottom: '12px' }}>🎁</div>
            <h2 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
              You've got a gift!
            </h2>
            <p style={{ color: '#c9a8ff', fontSize: '16px', margin: 0 }}>
              <strong style={{ color: '#fff' }}>{gift?.senderName}</strong> gifted you{' '}
              <strong style={{ color: '#fff' }}>{months} month{months > 1 ? 's' : ''}</strong> of Dreamy Bedtime{' '}
              <strong style={{ color: '#fff' }}>{planLabel}</strong>
            </p>
          </div>

          {/* Body */}
          <div className="p-4 p-lg-5">

            {/* Personal message */}
            {gift?.personalMessage && (
              <div className="p-3 rounded-3 mb-4"
                style={{ background: 'var(--purple-section)', borderLeft: '4px solid #6f42c1' }}>
                <p className="fst-italic mb-1 small" style={{ color: '#6f42c1' }}>"{gift.personalMessage}"</p>
                <p className="mb-0" style={{ fontSize: '12px', color: '#a090c0' }}>— {gift.senderName}</p>
              </div>
            )}

            {/* What's included */}
            <div className="mb-4 p-3 rounded-3" style={{ background: 'var(--page-bg)', border: '1px solid #e8d5ff' }}>
              <p className="fw-bold small mb-2" style={{ color: 'var(--text-main)' }}>What's included:</p>
              <ul className="list-unstyled mb-0">
                {(PLAN_PERKS[gift?.plan] || []).map(perk => (
                  <li key={perk} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '13px', color: '#444' }}>
                    <i className="bi bi-check2-circle" style={{ color: '#6f42c1' }}></i>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            {redeemError && (
              <div className="alert alert-danger py-2 mb-3 small">{redeemError}</div>
            )}

            {user ? (
              // Logged in — show claim button
              <>
                <p className="small text-muted mb-3">
                  Claiming as <strong>{user.email}</strong>.
                </p>
                <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold mb-3"
                  onClick={handleRedeem} disabled={redeeming}>
                  {redeeming
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Activating…</>
                    : <><i className="bi bi-gift-fill me-2"></i>Claim {months} Month{months > 1 ? 's' : ''} of {planLabel}</>}
                </button>
              </>
            ) : (
              // Not logged in — prompt to sign in / register
              <>
                <p className="small text-muted text-center mb-3">
                  Log in or create a free account to claim your gift.
                </p>
                <div className="d-grid gap-2">
                  <button className="btn btn-dream btn-lg rounded-pill fw-bold" onClick={goToLogin}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>Log in to claim
                  </button>
                  <button className="btn btn-outline-secondary rounded-pill" onClick={goToRegister}>
                    Create a free account
                  </button>
                </div>
                <p className="text-muted small text-center mt-3">
                  No credit card needed. Your gift will be waiting after you sign in.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftRedeemPage;
